import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { User, MapPin, Phone, Mail, PencilLine, CheckCircle2, Loader2, Send, MessageCircle } from "lucide-react";
import { WHATSAPP_ADMIN } from "@/lib/i18n";
import { useI18n } from "@/App";

const API = `/api`;
const STORE_KEY = "bz_user_profile";

const readProfile = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) || "null") || {}; }
  catch { return {}; }
};
const saveProfile = (p) => { try { localStorage.setItem(STORE_KEY, JSON.stringify(p)); } catch {} };

export default function QuoteModal({ product, open, onOpenChange }) {
  const { lang } = useI18n();
  const [form, setForm] = useState({ name: "", address: "", phone: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      const saved = readProfile();
      setForm({
        name: saved.name || "",
        address: saved.address || "",
        phone: saved.phone || "",
        email: saved.email || "",
        message: product ? `I'd like a quote for ${product.name}${product.spec ? ` (${product.spec})` : ""}.` : "",
      });
      setSuccess(false);
      setSubmitting(false);
    }
  }, [open, product]);

  if (!product) return null;
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const build = () => encodeURIComponent(
    [
      `🌱 *Biomass Zone — Quote Request*`,
      ``,
      `• *Product:* ${product.name}`,
      product.spec ? `• *Spec:* ${product.spec}` : null,
      `• *Price:* ${product.price}`,
      `• *Availability:* ${product.availability}`,
      ``,
      `• *Name:* ${form.name}`,
      `• *Phone:* ${form.phone}`,
      `• *Address:* ${form.address}`,
      form.email ? `• *Email:* ${form.email}` : null,
      form.message ? `• *Message:* ${form.message}` : null,
    ].filter(Boolean).join("\n")
  );

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim() || !form.phone.trim() || !form.address.trim()) {
      toast.error(lang === "HI" ? "नाम, फ़ोन और पता ज़रूरी है" : "Name, Phone and Address are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/submissions`, {
        role: "Buyer",
        name: form.name.trim(),
        phone: form.phone.trim(),
        location: form.address.trim(),
        email: form.email.trim() || null,
        notes: `[QUOTE REQUEST] ${product.name} — ${form.message || ""}`,
        fields: {
          product_id: product.id,
          product_name: product.name,
          spec: product.spec || "",
          price: product.price,
          availability: product.availability,
        },
      });
      saveProfile({ name: form.name, address: form.address, phone: form.phone, email: form.email });
      setSuccess(true);
      toast.success(lang === "HI" ? "कोटेशन रिक्वेस्ट भेजी गई" : "Quote request sent");
      setTimeout(() => onOpenChange(false), 1600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const Icon = ({ children }) => (
    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">{children}</span>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="quote-modal" className="bg-white sm:max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            <span className="text-emerald-700 text-xs tracking-widest uppercase font-semibold">
              {lang === "HI" ? "कोटेशन प्राप्त करें" : "Get Quote"}
            </span>
            <div className="font-display text-2xl mt-1 text-slate-900">{product.name}</div>
          </DialogTitle>
          <DialogDescription>
            <span className="text-slate-500 text-sm">
              {product.spec} · {product.price} · {product.availability}
            </span>
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="flex flex-col items-center py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <p className="text-lg font-display mt-3">{lang === "HI" ? "कोटेशन भेज दिया गया" : "Quote request sent"}</p>
            <p className="text-sm text-slate-500 mt-2 inline-flex items-center gap-1">
              <MessageCircle className="w-4 h-4 text-emerald-600" />
              {lang === "HI" ? "WhatsApp पर कन्फर्मेशन" : "WhatsApp confirmation opened"}
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="quote-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="relative">
                <input data-testid="quote-name" placeholder={lang === "HI" ? "नाम" : "Name"}
                  value={form.name} onChange={(e) => set("name", e.target.value)}
                  className="bz-input pr-10 bg-slate-50" />
                <Icon><User className="w-4 h-4" /></Icon>
              </div>
              <div className="relative">
                <input data-testid="quote-address" placeholder={lang === "HI" ? "पता" : "Address"}
                  value={form.address} onChange={(e) => set("address", e.target.value)}
                  className="bz-input pr-10 bg-slate-50" />
                <Icon><MapPin className="w-4 h-4" /></Icon>
              </div>
              <div className="relative">
                <input data-testid="quote-phone" placeholder={lang === "HI" ? "फ़ोन नंबर" : "Phone Number"}
                  value={form.phone} onChange={(e) => set("phone", e.target.value)}
                  className="bz-input pr-10 bg-slate-50" />
                <Icon><Phone className="w-4 h-4" /></Icon>
              </div>
              <div className="relative">
                <input data-testid="quote-email" type="email" placeholder={lang === "HI" ? "ईमेल" : "Email Address"}
                  value={form.email} onChange={(e) => set("email", e.target.value)}
                  className="bz-input pr-10 bg-slate-50" />
                <Icon><Mail className="w-4 h-4" /></Icon>
              </div>
            </div>
            <div className="relative">
              <textarea data-testid="quote-message" placeholder={lang === "HI" ? "आपके मन में क्या है" : "What's on your mind"}
                value={form.message} onChange={(e) => set("message", e.target.value)}
                className="bz-input min-h-[110px] pr-10 bg-slate-50" />
              <Icon><PencilLine className="w-4 h-4" /></Icon>
            </div>
            <button type="submit" data-testid="quote-submit" disabled={submitting}
              className="w-full mt-3 rounded-xl py-4 font-bold text-white text-base tracking-wide inline-flex items-center justify-center gap-2 transition"
              style={{ background: submitting ? "#f97316aa" : "#f97316", boxShadow: "0 12px 28px -12px rgba(249,115,22,0.55)" }}>
              {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> {lang === "HI" ? "भेजा जा रहा है…" : "SENDING…"}</>)
                : (<><Send className="w-4 h-4" /> {lang === "HI" ? "अभी मैसेज भेजें" : "SEND MESSAGE NOW"}</>)}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
